/**
 * FacebookAdLanding
 *
 * Marketing landing page aimed at paid traffic. Forces light mode, shows a
 * demo video, feature highlights, pricing summary, testimonials, and footer.
 */
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import SEOHead from '@/components/shared/SEOHead';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { PLAN_IDS, PLAN_FEATURES } from '@/data/pricing/pricingPlans';
import Footer from '@/components/shared/Footer';
import { kevinMeetingUrl } from '@/data/scheduling/schedulingLinks';

// Lazy-load heavy components to reduce initial bundle
const FeaturesSectionLazy = lazy(() =>
  import('@/components/landing/sections/FeaturesSection/FeaturesSection').then(m => ({ default: m.FeaturesSection }))
);
const HeroVideoDialogLazy = lazy(() =>
  import('@/components/ui/modals/hero-video-dialog').then(m => ({ default: m.default || m }))
);
// Removed direct import of HeroVideoDialog to keep it out of initial bundle

export default function FacebookAdLanding() {
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const featuresRef = useRef(null);
  const [featuresInView, setFeaturesInView] = useState(false);
  const featureImages = {
    'templates': { image: '/images/screenshots/screen1-ad.png' },
    'template-details': { image: '/images/screenshots/screen2-ad.png' },
    'ai-tools': { image: '/images/screenshots/screen3-ad.png' },
    'video-testimonials': { image: '/images/screenshots/screen4-ad.png' },
  };

  // Handle thumbnail loading
  useEffect(() => {
    const img = new Image();
    img.onload = () => setIsVideoLoading(false);
    img.onerror = () => setIsVideoLoading(false);
    img.src = "/images/hero/video.png";
  }, []);

  // Defer loading FeaturesSection until in-view
  useEffect(() => {
    const el = featuresRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setFeaturesInView(true);
          observer.disconnect();
        }
      });
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Preload feature images for instant switching on this page
  useEffect(() => {
    const paths = Object.values(featureImages).map(v => v.image).filter(Boolean);
    const imgs = paths.map(src => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = src;
      return img;
    });
    return () => {
      // no cleanup needed for Image objects
    };
  }, []);

  // Force light mode on this page only
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');

    const observer = new MutationObserver(() => {
      if (root.classList.contains('dark')) root.classList.remove('dark');
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  // Features are now rendered using FeaturesSection

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen bg-white">
      <SEOHead 
        pageType="facebook-ad-landing"
        title="500+ Proven Political Ad Templates That Win Campaigns | Advocacy Lab"
        description="Join the top campaigns and firms in politics by starting your FREE trial today. Professional political ad templates for Facebook, Instagram, and all digital platforms."
      />
      


      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src="/images/logos/logo-big-colored.png"
            alt="Advocacy Lab"
            className="mx-auto h-17 w-auto mb-12"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
          
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
            <span className="block">500+ Proven Political</span>
            <span className="block">Ad Templates That</span>
            <span className="block text-purple-600">Win Campaigns</span>
          </h1>
          
          <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
            Join the top campaigns and firms in politics by starting your <strong>FREE</strong> trial today.
          </p>
          
          {/* Demo Video Section - moved here */}
          <div className="mt-10 mb-10">
            <div className="mx-auto max-w-5xl">
              <div className="relative">
          {isVideoLoading ? (
                  <div className="w-full aspect-video bg-white border border-gray-200 rounded-lg animate-pulse flex items-center justify-center">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-l-[16px] border-l-gray-400 border-y-[12px] border-y-transparent ml-1"></div>
                    </div>
                  </div>
                ) : (
              <Suspense fallback={<div className="w-full aspect-video bg-white border border-gray-200 rounded-lg" /> }>
                <HeroVideoDialogLazy
                  className="w-full"
                  animationStyle="from-center"
                  videoSrc="https://www.youtube.com/embed/taRKE1O4RDQ?si=4rb-zSdDkVK9qxxb"
                  thumbnailSrc="/images/hero/video.png"
                  thumbnailAlt="Advocacy Lab Demo Video"
                />
              </Suspense>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-10">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center rounded-md bg-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 w-full sm:w-auto"
            >
              Start free trial
            </Link>
          </div>
        </div>
      </section>

      

       {/* Powerful Tools Section (keep existing title, use FeaturesSection content) */}
       <section className="py-24 bg-white" ref={featuresRef}>
         <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Powerful Tools
            </h2>
            <h3 className="mt-4 text-3xl font-semibold text-gray-900">
              for <span className="text-purple-600">Political Creators</span>
            </h3>
          </div>
          {featuresInView ? (
            <Suspense fallback={<div className="h-56" /> }>
              <FeaturesSectionLazy 
                hideHeader 
                compactTop 
                ctaText="Start free trial" 
                ctaTo="/signup" 
                mediaOverrides={featureImages}
                staticImages
                ctaLarge
              />
            </Suspense>
          ) : (
             <div className="h-56" />
           )}
        </div>
      </section>

      {/* Pricing Section (moved below Features) */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          
          {/* Premium Plan Card */}
          <div className="relative rounded-2xl bg-gradient-to-b from-indigo-500 to-purple-600 shadow-xl overflow-hidden scale-105 mx-auto max-w-md">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-white">Premium</h2>
              <p className="mt-2 text-sm text-indigo-100 max-w-xs">
                Complete creative suite with all features to save you significant time and money.
              </p>
              
              <div className="mt-6">
                {/* Show original pricing crossed out with discount badge */}
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-lg line-through text-indigo-100/60">$49</span>
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    30% off for limited time
                  </span>
                </div>
                
                <p className="text-5xl font-bold tracking-tight text-white flex items-baseline">
                  <span>$34</span>
                  <span className="text-sm font-normal ml-1 opacity-60">/mo</span>
                </p>
                <div className="text-sm leading-5 mt-1">
                  <p className="text-indigo-100/80">Billed monthly</p>
                </div>
              </div>
              
              <div className="mt-8">
                <Link
                  to="/signup"
                  className="bg-white text-purple-600 hover:bg-gray-50 block w-full rounded-lg px-3 py-3 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200"
                >
                  Start free trial
                </Link>
              </div>
              
              <div className="mt-8 space-y-4">
                {(PLAN_FEATURES[PLAN_IDS.PREMIUM] || []).map((feature) => (
                  <div key={feature} className="flex gap-x-3">
                    <div className="bg-white/20 text-white flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                      <Check className="h-4 w-4" />
                    </div>
                    <p className="text-white text-sm leading-6">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Enterprise Contact */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Have questions about the platform?
              <a 
                href={kevinMeetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 font-semibold ml-1"
              >
                Book a call to learn more
              </a>
            </p>
          </div>
          
        </div>
      </section>

             {/* Testimonial Section */}
       <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          {/* 5 Star Rating */}
          <div className="flex justify-center mb-8">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className="w-8 h-8 text-yellow-400 fill-current"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ))}
          </div>

          <blockquote className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
            "With Advocacy Lab we're putting out triple the content, in half the time."
          </blockquote>
          
                     <div className="flex items-center justify-center">
             <div className="flex-shrink-0">
              <img
                 className="h-16 w-16 rounded-full"
                 src="/images/ad/ad1.jpg"
                 alt="Joey S."
                loading="lazy"
                decoding="async"
                width="64"
                height="64"
               />
             </div>
            <div className="ml-4 text-left">
              <div className="text-lg font-semibold text-gray-900">Joey S.</div>
              <div className="text-gray-600">Campaign Manager, Michigan</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/signup"
            className="inline-flex items-center justify-center rounded-md bg-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 w-full sm:w-auto"
          >
            Start free trial
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
} 